/**
 * 请求封装。所有对 sidecar 的请求都经这里，页面不直接用 fetch（做法照参照实现 console/fulfillment/app）。
 *
 * - 只请求同源的 /api/...（nginx 转给 sidecar，带上本后台的通行证 cookie）。
 * - 超时：读 15 秒、写 30 秒（写只有登记查看理由一处，不重试）。
 * - 成功返回解析好的 JSON；失败一律抛 ApiError，code 见 api/types.ts 的 ApiErrorCode。
 *   detail 里带认得的 code 就用它（查看理由那几个），否则按 HTTP 状态归；detail 的其余字段（如 sn、env）
 *   放在 `data` 里给界面用。`message` 只给研发看，界面不按它分支。
 * - 401 广播给外壳（app/auth.tsx）：通行证过期了，去入口换一张。
 */
import { DETAIL_CODES, type ApiErrorCode } from './types';

export const READ_TIMEOUT_MS = 15_000;
export const WRITE_TIMEOUT_MS = 30_000;

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  /** HTTP 状态；请求没发出或没有响应时为 null */
  readonly status: number | null;
  /** detail 是对象时的全部字段（例：reason_required 带的 sn、env）；否则为 null */
  readonly data: Record<string, unknown> | null;

  constructor(code: ApiErrorCode, message: string, status: number | null = null, data: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

type Listener = () => void;
const unauthenticated = new Set<Listener>();

export const apiEvents = {
  /** 收到 401：外壳把人送去入口换通行证 */
  onUnauthenticated(fn: Listener): () => void {
    unauthenticated.add(fn);
    return () => unauthenticated.delete(fn);
  },
};

export type Query = Record<string, string | number | boolean | null | undefined>;

export function buildUrl(path: string, query?: Query): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

function codeForStatus(status: number): ApiErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 503) return 'unavailable';
  return status >= 500 ? 'server_error' : 'bad_response';
}

/** FastAPI 的错误体是 {"detail": "..."} 或 {"detail": {"code", "message", ...}}；拿不到就用 HTTP 状态拼一句 */
function errorOf(body: unknown, status: number): ApiError {
  const d = typeof body === 'object' && body !== null ? (body as { detail?: unknown }).detail : undefined;
  if (typeof d === 'object' && d !== null) {
    const data = d as Record<string, unknown>;
    const known = DETAIL_CODES.find((c) => c === data.code);
    const message = typeof data.message === 'string' && data.message ? data.message : `HTTP ${status}`;
    return new ApiError(known ?? codeForStatus(status), message, status, data);
  }
  return new ApiError(codeForStatus(status), typeof d === 'string' && d ? d : `HTTP ${status}`, status);
}

/** 读接口：成功返回数据，失败抛 ApiError */
export async function apiGet<T>(path: string, query?: Query, signal?: AbortSignal): Promise<T> {
  return request<T>('GET', buildUrl(path, query), undefined, READ_TIMEOUT_MS, signal);
}

/** 写接口（JSON）：成功返回数据，失败抛 ApiError。不自动重试（queries.ts 的 mutations.retry=false） */
export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return request<T>('POST', path, body, WRITE_TIMEOUT_MS);
}

async function request<T>(method: 'GET' | 'POST', url: string, body: unknown, timeoutMs: number, signal?: AbortSignal): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new ApiError('network_offline', '网络断开了，恢复后会自动重试');
  }
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, timeoutMs);
  const onOuterAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onOuterAbort);

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers: method === 'POST' ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: method === 'POST' ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
      signal: ctrl.signal,
    });
  } catch (e) {
    if (timedOut) throw new ApiError('timeout', '数据接口没有及时响应');
    if (signal?.aborted) throw e;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    throw offline
      ? new ApiError('network_offline', '网络断开了，恢复后会自动重试')
      : new ApiError('server_unreachable', '连不上问题诊断的数据接口');
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onOuterAbort);
  }

  let payload: unknown = null;
  let parsed = true;
  try {
    payload = await res.json();
  } catch {
    parsed = false;
  }
  if (!res.ok) {
    const err = errorOf(payload, res.status);
    if (err.code === 'unauthenticated') for (const fn of [...unauthenticated]) fn();
    throw err;
  }
  if (!parsed) throw new ApiError('bad_response', `数据接口没有按约定返回（HTTP ${res.status}）`, res.status);
  return payload as T;
}

/** 读失败要不要自动重试：只重试服务器 / 网络这一类，4xx 不重试 */
export function isRetryableRead(e: unknown): boolean {
  return isApiError(e) && ['server_unreachable', 'timeout', 'server_error', 'unavailable', 'access_log_unavailable'].includes(e.code);
}
